/**
 * User Profile Form Component
 *
 * Form for updating user profile information with avatar upload
 */

import { zodResolver } from '@hookform/resolvers/zod'
import {
  type UpdateUser,
  UpdateUserSchema,
  type UserResponse,
} from '@monorepo/shared/schemas/users'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Upload } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from '@/lib/toast'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'
import { userService } from '../services/userService'

interface UserProfileFormProps {
  user: UserResponse
  className?: string
}

export function UserProfileForm({ user, className }: UserProfileFormProps) {
  const queryClient = useQueryClient()
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
  } = useForm<UpdateUser>({
    resolver: zodResolver(UpdateUserSchema),
    mode: 'onChange',
    defaultValues: {
      name: user.name,
      phone: user.phone || undefined,
      timezone: user.timezone || 'UTC',
      language: user.language || 'en',
      avatar_url: user.avatar_url || undefined,
    },
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateUser) => userService.updateProfile(data),
    onSuccess: _updatedUser => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      toast.success('Profile updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update profile')
    },
  })

  const onSubmit = async (data: UpdateUser) => {
    await updateProfileMutation.mutateAsync(data)
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB')
      return
    }

    try {
      setUploadingAvatar(true)

      const { avatar_url } = await userService.uploadAvatar(file)

      // Update form with new avatar URL
      setValue('avatar_url', avatar_url)

      // Invalidate queries to refresh user data
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })

      toast.success('Avatar uploaded successfully')
    } catch {
      toast.error('Failed to upload avatar')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const currentAvatarUrl = watch('avatar_url') || user.avatar_url

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-6', className)}
    >
      <Card className='bg-transparent'>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          {/* Avatar */}
          <div className='flex items-center gap-6'>
            <Avatar className='h-24 w-24'>
              <AvatarImage
                src={currentAvatarUrl || undefined}
                alt={user.name}
              />
              <AvatarFallback className='text-2xl'>
                {user.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <Label htmlFor='avatar' className='cursor-pointer'>
                <div className='flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent transition-colors'>
                  {uploadingAvatar ? (
                    <>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className='h-4 w-4' />
                      <span>Upload Avatar</span>
                    </>
                  )}
                </div>
                <input
                  id='avatar'
                  type='file'
                  accept='image/*'
                  className='sr-only'
                  onChange={handleAvatarUpload}
                  disabled={uploadingAvatar}
                />
              </Label>
              <p className='text-xs text-muted-foreground mt-2'>
                JPG, PNG or GIF. Max size 2MB.
              </p>
            </div>
          </div>

          {/* Name */}
          <div className='grid gap-2'>
            <Label htmlFor='name'>
              Name <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='name'
              placeholder='Your full name'
              {...register('name')}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className='text-sm text-red-500'>{errors.name.message}</p>
            )}
          </div>

          {/* Email (read-only) */}
          <div className='grid gap-2'>
            <Label htmlFor='email'>Email</Label>
            <Input
              id='email'
              value={user.email}
              disabled
              className='bg-muted cursor-not-allowed'
            />
            <p className='text-xs text-muted-foreground'>
              Email cannot be changed. Contact support if you need to update it.
            </p>
          </div>

          {/* Phone */}
          <div className='grid gap-2'>
            <Label htmlFor='phone'>Phone</Label>
            <Input
              id='phone'
              type='tel'
              placeholder='+1 (555) 000-0000'
              {...register('phone')}
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && (
              <p className='text-sm text-red-500'>{errors.phone.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className='bg-transparent'>
        <CardHeader>
          <CardTitle>Regional Settings</CardTitle>
          <CardDescription>
            Configure your timezone and language preferences
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Timezone */}
          <div className='grid gap-2'>
            <Label htmlFor='timezone'>Timezone</Label>
            <Select
              value={watch('timezone')}
              onValueChange={value => setValue('timezone', value)}
            >
              <SelectTrigger
                className={errors.timezone ? 'border-red-500' : ''}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='UTC'>UTC</SelectItem>
                <SelectItem value='America/New_York'>
                  Eastern Time (ET)
                </SelectItem>
                <SelectItem value='America/Chicago'>
                  Central Time (CT)
                </SelectItem>
                <SelectItem value='America/Denver'>
                  Mountain Time (MT)
                </SelectItem>
                <SelectItem value='America/Los_Angeles'>
                  Pacific Time (PT)
                </SelectItem>
                <SelectItem value='Europe/London'>London (GMT)</SelectItem>
                <SelectItem value='Europe/Paris'>Paris (CET)</SelectItem>
                <SelectItem value='Asia/Tokyo'>Tokyo (JST)</SelectItem>
                <SelectItem value='Asia/Shanghai'>Shanghai (CST)</SelectItem>
              </SelectContent>
            </Select>
            {errors.timezone && (
              <p className='text-sm text-red-500'>{errors.timezone.message}</p>
            )}
          </div>

          {/* Language */}
          <div className='grid gap-2'>
            <Label htmlFor='language'>Language</Label>
            <Select
              value={watch('language')}
              onValueChange={value => setValue('language', value)}
            >
              <SelectTrigger
                className={errors.language ? 'border-red-500' : ''}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='en'>English</SelectItem>
                <SelectItem value='es'>Spanish</SelectItem>
                <SelectItem value='fr'>French</SelectItem>
                <SelectItem value='de'>German</SelectItem>
                <SelectItem value='ja'>Japanese</SelectItem>
                <SelectItem value='zh'>Chinese</SelectItem>
              </SelectContent>
            </Select>
            {errors.language && (
              <p className='text-sm text-red-500'>{errors.language.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className='flex justify-end'>
        <Button
          type='submit'
          disabled={!isValid || !isDirty || updateProfileMutation.isPending}
        >
          {updateProfileMutation.isPending ? (
            <>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}
